<template>
    <ModalBase :show-modal="true" @close-modal="close" :auto-h="true" :narrow="true" :overlay-scrollb="false" class="add-supplier-modal">
        <template v-slot:titlebar>
            <TitleBar variant-class="title-modal title-modal--simple">
                <template v-slot:title>
                    <span>{{ $gettext('We found') }}&nbsp;{{ supplierList.length }}&nbsp;{{ supplierList.length === 1 ? $gettext('new supplier') : $gettext('new suppliers') }}</span>
                </template>
                <template v-slot:subtitle>
                    <span class="text-unassigned">{{ $gettext('Do you want to add this supplier to your contact list?') }}</span>
                </template>
            </TitleBar>
        </template>
        <template v-slot:maincontent>
            <transition name="supplier-form-switch" mode="out-in">
                <NewSupplierForm
                    ref="newSupplierForm"
                    :key="supplierFormKey"
                    v-model="newSupplierData"
                />
            </transition>
        </template>
        <template v-slot:actions>
            <div class="supplier-actions-row">
                <div class="supplier-actions-counter">
                    <span class="supplier-counter-badge">
                        {{ displaySupplierIndex }} / {{ supplierList.length }}
                    </span>
                </div>
                <div class="supplier-actions-buttons">
                    <GekkoButton
                        @click="skipOrFinish()"
                        icon="fa-solid"
                        :text="buttonSkipText"
                        variant="link"
                        class="ml-auto" />
                    <GekkoButton
                        @click="saveAndGetNext()"
                        icon="fa-solid fa-save"
                        :text="$gettext('Save')"
                        class="ml-auto" />
                </div>
            </div>
        </template>
    </ModalBase>
</template>

<script>
import ModalBase from '@/components/base_components/ModalBase'
import GekkoButton from '@/components/GekkoButton'
import TitleBar from '@/components/TitleBar'
import NewSupplierForm from '@/components/costs/ui/NewSupplierForm.vue'
import CustomerService from '@/store/services/customer.service'
import CostService from '@/store/services/cost.service'
import { mapGetters } from 'vuex'

export default {
    name: 'AddSupplierListModal',
    components: {
        ModalBase,
        TitleBar,
        GekkoButton,
        NewSupplierForm
    },
    props: {
        supplierList: {
            type: Array,
            default: () => []
        }
    },
    created() {
        if (this.hasSupplierAtIndex) {
            this.newSupplierData = { ...(this.supplierList[this.savingIndex].new_supplier || {}) }
            return
        }

        this.newSupplierData = null
    },
    data() {
        return {
            newSupplierData: null,
            savingIndex: 0
        }
    },
    computed: {
        ...mapGetters({
            getCompanyId: 'main/getCompanyId'
        }),
        hasSupplierAtIndex() {
            return this.savingIndex >= 0 && this.savingIndex < this.supplierList.length && this.savingIndex in this.supplierList
        },
        supplierFormKey() {
            const currentSupplier = this.supplierList[this.savingIndex]
            return currentSupplier?.cost_id || `supplier-form-${this.savingIndex}`
        },
        displaySupplierIndex() {
            if (!this.supplierList.length) {
                return 0
            }

            return Math.min(this.savingIndex + 1, this.supplierList.length)
        },
        buttonSkipText() {
            if (this.savingIndex >= this.supplierList.length - 1) {
                return this.$gettext('Done')
            }
            return this.$gettext('Skip')
        }
    },
    methods: {
        close() {
            this.$emit('close')
        },
        saveAndGetNext() {
            this.newSupplierData.is_supplier = true
            this.newSupplierData.is_customer = false
            this.$refs.newSupplierForm.assignTaxIdFields()
            this.newSupplierData.owner_company = this.getCompanyId

            CustomerService.create(this.newSupplierData).then((response) => {
                this.$store.commit('setAlert', {
                    title: this.$gettext("Contact added"),
                    text: this.$gettext("The supplier was added to your contact list."),
                    variant: "success",
                    duration: 4
                })

                const cost = {
                    number: this.supplierList[this.savingIndex].cost_number,
                    owner_company: this.supplierList[this.savingIndex].cost_owner_company,
                    is_automatically_scanned: true,
                    supplier: response.data.id
                }

                CostService.update(this.supplierList[this.savingIndex].cost_id, cost).then(() => {
                    this.$emit('created')

                    this.savingIndex += 1
                    if (!this.hasSupplierAtIndex) {
                        this.close()
                        return
                    }
                    
                    this.newSupplierData = { ...(this.supplierList[this.savingIndex].new_supplier || {}) }
                }).catch(() => {
                    this.$store.commit('setAlert', {
                        title: this.$gettext("Error adding contact"),
                        text: this.$gettext('The contact was added but we could not link it to the cost. Please check your contact list and link it manually.'),
                        variant: "danger",
                        duration: 4
                    })
                })
            }).catch((error) => {
                let msg = this.$gettext('The contact could not be added to your contact list.')
                if (error.name) {
                    msg = this.$gettext('Contact name already exists')
                }

                this.$store.commit('setAlert', {
                    title: this.$gettext("Error adding contact"),
                    text: msg,
                    variant: "danger",
                    duration: 4
                })
            })
        },
        skipOrFinish() {
            if (!this.hasSupplierAtIndex) {
                this.close()
                return
            }

            this.savingIndex += 1
            if (!this.hasSupplierAtIndex) {
                this.close()
                return
            }
            
            this.newSupplierData = { ...(this.supplierList[this.savingIndex].new_supplier || {}) }
        }
    }
}
</script>

<style lang="scss" scoped>
.supplier-form-switch-enter-active,
.supplier-form-switch-leave-active {
    transition: opacity 0.28s ease, transform 0.28s ease, filter 0.28s ease;
}

.supplier-form-switch-enter,
.supplier-form-switch-leave-to {
    opacity: 0;
    transform: translateY(8px);
    filter: brightness(0.92);
}

.supplier-form-switch-enter-to,
.supplier-form-switch-leave {
    opacity: 1;
    transform: translateY(0);
    filter: brightness(1);
}

.supplier-counter-badge {
    display: inline-flex;
    align-items: center;
    padding: 0.3rem 0.65rem;
    border-radius: 999px;
    background: #eef1f4;
    color: #5f6b76;
    font-size: 0.85rem;
    font-weight: 600;
    line-height: 1;
}

.supplier-actions-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 1rem;
    width: 100%;
}

.supplier-actions-counter {
    display: flex;
    align-items: center;
}

.supplier-actions-buttons {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 0.75rem;
    margin-left: auto;
}
</style>